import React, { useState, useEffect } from "react";
import { BillingService } from "../../Services/BillingService";
import { StorageUtils } from "../../Utils/StorageUtils";

const PricingPlan = ({ data, onBuy, userStatus = 'starter', planDisplayName = 'Starter Plan' }) => {
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  
  // Debug: Log when component receives new props
  console.log('🎨 PricingPlan render:', {
    userStatus,
    planDisplayName,
    isPro: userStatus === 'pro',
    timestamp: new Date().toISOString()
  });
  
  // Track when userStatus prop changes
  useEffect(() => {
    console.log('⚙️ PricingPlan: userStatus changed to:', userStatus);
    console.log('🏅 PricingPlan: planDisplayName changed to:', planDisplayName);
  }, [userStatus, planDisplayName]);
  // Keep only subscription-style plans (interval present), prefer monthly
  const subscriptionPlans = Array.isArray(data) ? data.filter(p => !!p?.interval) : [];
  const plan = subscriptionPlans.find(p => p.interval === 'month') || subscriptionPlans[0];

  const displayAmount = (plan?.amount ?? 0) / 100;

  // Inclusive description that covers non-business users as well
  const inclusiveDescription = 'Advanced features for individuals, creators, teams, and businesses.';
  const description = (() => {
    const desc = plan?.description || '';
    // If backend description is narrowly business-focused, override with inclusive copy
    if (/\bgrowing\s+business(es)?\b|\bbusiness(es)?\b/i.test(desc)) {
      return inclusiveDescription;
    }
    return desc || inclusiveDescription;
  })();

  const handleSubscribe = () => {
    if (onBuy && plan) onBuy(plan);
  };

  const handleManageSubscription = async () => {
    setIsLoadingPortal(true);
    
    try {
      console.log('🔧 Opening billing portal...');
      console.log('🔑 Token check:', StorageUtils.getUserData()?.token ? 'Token exists' : 'No token found');
      console.log('🌍 Environment:', process.env.NODE_ENV);
      console.log('🔗 API Base URL:', window.location.origin);
      
      const response = await BillingService.createBillingPortalSession();
      console.log('📡 Full API Response:', response);
      
      if (response.data?.url) {
        console.log('✅ Portal URL received:', response.data.url);
        window.location.href = response.data.url;
      } else {
        console.log('❌ No portal URL in response:', response.data);
        throw new Error('No portal URL received from server');
      }
    } catch (error) {
      console.error('❌ Billing portal error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      let errorMessage = 'Unable to open billing portal.';
      
      if (error.response?.status === 404) {
        errorMessage = 'Billing portal endpoint not found. The backend may not support this feature yet.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You may not have an active subscription.';
      } else if (error.response?.data?.error || error.response?.data?.message) {
        errorMessage = error.response.data.error || error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Handle specific billing portal errors with better guidance
      if (errorMessage.includes('No billing history found')) {
        const upgradeMessage = `No Billing History Found

You need to upgrade to Pro first before you can manage your subscription.

✨ Click the "Subscribe" button below to get started!

Once you've subscribed, you'll be able to access the billing portal to:
• View your subscription details
• Update payment methods
• Cancel your subscription`;
        alert(upgradeMessage);
      } else if (errorMessage.includes('No configuration provided') || errorMessage.includes('default configuration has not been created')) {
        const configMessage = `Subscription Management Temporarily Unavailable

We're setting up the subscription management portal.

📧 To manage your subscription:
• Email: support@threadshare.com
• Include: Your account email and what you'd like to change
• We'll help you:
  - Cancel your subscription
  - Update payment methods
  - View billing history

We typically respond within 24 hours.

Sorry for the inconvenience!`;
        alert(configMessage);
      } else if (error.response?.status === 404) {
        const fallbackMessage = `Billing portal is not yet available.

To manage your subscription:

• Email: support@threadshare.com
• Include: Account email and request
• We'll help you update/cancel subscription

Sorry for the inconvenience!`;
        alert(fallbackMessage);
      } else {
        alert(errorMessage);
      }
      
      setIsLoadingPortal(false);
    }
  };

  // Show different content based on user status
  if (userStatus === 'pro') {
    return (
      <div className="container my-5">
        <h2 className="text-center mb-2" style={{ fontWeight: 800 }}>Plans & Pricing</h2>
        <p className="text-center text-muted mb-5">
          You're all set with your Pro subscription!
        </p>

        <div className="d-flex justify-content-center">
          <div className="card shadow-xl rounded-2xl" style={{ maxWidth: "440px", width: "100%", border: '2px solid #10b981' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center mb-2">
                <span className="badge rounded-pill" style={{ background: '#ecfdf5', color: '#10b981' }}>✅ Active</span>
              </div>
              <h3 className="mb-3" style={{ fontWeight: 800 }}>Pro Plan - Monthly</h3>

              <div className="d-flex align-items-end mb-3">
                <span className="me-1" style={{ fontSize: '1.25rem' }}>$</span>
                <span style={{ fontSize: '2.25rem', fontWeight: 800, lineHeight: 1 }}>
                  {displayAmount.toFixed(2)}
                </span>
                <span className="ms-2 text-muted">/ month</span>
              </div>

              <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
                {description}
              </p>

              <ul className="list-unstyled mb-4">
                {(plan?.features || ['Unlimited link tracking', 'Advanced analytics dashboard']).map((feature, i) => (
                  <li key={i} className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill me-2" style={{ color: '#10b981' }}></i>
                    <span className="text-muted" style={{ fontSize: '0.95rem' }}>{feature}</span>
                  </li>
                ))}
              </ul>

              <button className="btn btn-success w-100 rounded-pill" disabled>
                ✅ Currently Subscribed
              </button>
              <button 
                className="btn btn-outline-primary w-100 rounded-pill mt-2" 
                onClick={handleManageSubscription}
                disabled={isLoadingPortal}
                style={{ fontSize: '0.9rem' }}
              >
                {isLoadingPortal ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading...
                  </>
                ) : (
                  '⚙️ Manage Subscription'
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 text-secondary">
          Enjoying Pro? Thanks for your support! 🎉
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="container my-5">
        <h2 className="text-center mb-4">Plans & Pricing</h2>
        <p className="text-center text-muted">No subscription plans are available right now.</p>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <h2 className="text-center mb-2" style={{ fontWeight: 800 }}>Plans & Pricing</h2>
      <p className="text-center text-muted mb-5">
        No shady charges, no unexpected shocks. One transparent subscription.
      </p>

      <div className="d-flex justify-content-center">
        <div className="card shadow-xl rounded-2xl" style={{ maxWidth: "440px", width: "100%", border: '1px solid #e5e7eb' }}>
          <div className="card-body p-4">
            <div className="d-flex align-items-center mb-2">
              <span className="badge rounded-pill" style={{ background: '#e8f2ff', color: '#1D4ED8' }}>Pro</span>
            </div>
            <h3 className="mb-3" style={{ fontWeight: 800 }}>{plan.name || 'Pro Plan - Monthly'}</h3>

            <div className="d-flex align-items-end mb-3">
              <span className="me-1" style={{ fontSize: '1.25rem' }}>$</span>
              <span style={{ fontSize: '2.25rem', fontWeight: 800, lineHeight: 1 }}>
                {displayAmount.toFixed(2)}
              </span>
              <span className="ms-2 text-muted">/ {plan.interval}</span>
            </div>

            <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
              {description}
            </p>

            <ul className="list-unstyled mb-4">
              {(plan.features || []).map((feature, i) => (
                <li key={i} className="d-flex align-items-start mb-2">
                  <i className="bi bi-check-circle-fill me-2" style={{ color: '#10b981' }}></i>
                  <span className="text-muted" style={{ fontSize: '0.95rem' }}>{feature}</span>
                </li>
              ))}
            </ul>

            <button className="btn btn-primary w-100 rounded-pill glow-button" onClick={handleSubscribe}>
              Subscribe
            </button>
            <p className="text-center text-muted mt-2 mb-0" style={{ fontSize: '0.9rem' }}>Cancel anytime</p>
          </div>
        </div>
      </div>

      <div className="text-center mt-4 text-secondary">
        {userStatus === 'starter' ? (
          <>Currently on the {planDisplayName}. Upgrade to unlock unlimited features!</>
        ) : (
          'Looking for a free option? You\'re on the Starter Plan.'
        )}
      </div>
    </div>
  );
};

export default PricingPlan;
